<i18n>
{
	"en": {
		"dateTime": "Date and time",
		"customerFault": "Customer fault",
		"deviationMessage": "Description of the deviation",
		"discoveredAt": "Discovered at",
		"discoveredBy": "Discovered by / ref.",
		"cost": "Cost",
		"vendor": "Vendor",
		"addTripOrders": "Add all orders associated with the trip",
		"actionDescription": "Description of actions",
		"actionDate": "Date for actions"
	},
	"no": {
		"dateTime": "Dato og tid",
		"customerFault": "Kundefeil",
		"deviationMessage": "Beskrivelse av avviket",
		"discoveredAt": "Oppdaget ved",
		"discoveredBy": "Oppdaget av / ref.",
		"cost": "Kostnad",
		"vendor": "Leverandør",
		"addTripOrders": "Legg til alle oppdrag knyttet til turen",
		"actionDescription": "Beskrivelse av tiltak",
		"actionDate": "Dato for tiltak"
	}
}
</i18n>

<template>
	<div class="main">
		<div class="top">
			<ClerkInput
				:value="deviation.clerk"
				focusNextOnSelected
				@change="setClerk"
			/>
			<t-date-time-input
				:title="$t('dateTime')"
				:dateTimeObject="deviation.date"
				@date-input="setDateTime({ dateValue: $event, hasTime: null })"
				@time-input="setDateTime({ timeValue: $event, hasTime: Boolean($event) })"
			/>
			<DeviationTypeInput
				:value="deviation.deviationType"
				@change="setDeviationType"
			/>
			<DeviationStatusInput
				:value="deviation.status"
				@change="setStatus"
			/>
			<t-checkbox
				style="margin-top: 22px; margin-bottom: 0px;"
				:label="$t('customerFault')"
				:checked="deviation.customerFault"
				@change="setCustomerFault"
			/>
		</div>

		<hr >
		<div class="message">
			<t-text-area
				:label="$t('deviationMessage')"
				:value="deviation.message"
				height="100%"
				@input="setMessage"
			/>
		</div>

		<div class="details">
			<DeviationCauseInput
				:value="deviation.deviationCause"
				@change="setDeviationCause"
			/>
			<GoodsTypesFormInput
				:selectedItem="deviation.goodsType"
				enableFocusNextOnKeyboardSelect
				@change="setGoodsType"
			/>
			<t-text-input
				:label="$t('discoveredAt')"
				:value="deviation.discoveredAt"
				@input="setDiscoveredAt"
			/>
			<t-text-input
				:label="$t('discoveredBy')"
				:value="deviation.discoveredBy"
				@input="setDiscoveredBy"
			/>
		</div>

		<hr >
		<div class="customer-vendor">
			<CustomerInput
				ref="customer"
				:value="deviation.customer"
				disableButtonTabbing
				hidePadding
				@change="setCustomer($event) && Object.keys($event).length
					&& focusNext({ target: $refs.customer.$el.querySelector('input') })"
			/>
			<VendorInput
				ref="vendor"
				:label="$t('vendor')"
				:value="deviation.vendor"
				@change="setVendor"
			/>
		</div>

		<div class="terminal-cost">
			<TripAddressInput
				title="Terminal"
				:value="deviation.terminal || {}"
				focusNextOnSelected
				onlyTerminals
				@change="setTerminal"
			/>
			<t-number-input
				castToNumber
				:label="$t('cost')"
				:value="deviation.cost"
				@input="setCost"
			/>
		</div>

		<LicencePlates
			class="licenceplates"
			:licencePlates="deviation.licencePlates"
			@change="setLicencePlates"
		/>
		<hr >

		<div class="trip">
			<TripsSearchInput
				:selectedTrip="trip"
				style="grid-area: input;"
				:defaultValue="{}"
				@change="setTrip"
			>
				<template #input-card-after>
					<t-icon-button
						:title="$t('addTripOrders')"
						icon="add-to-artifact"
						type="bp"
						appearance="minimal"
						disableBoxShadow
						:tabindex="-1"
						@click="addTripOrders"
					/>
				</template>
			</TripsSearchInput>
		</div>

		<div class="orders">
			<OrdersMultiselect
				:selectedItems="orders || []"
				@select="addOrder"
				@remove="removeOrderBinding"
			/>
			<t-tag-container
				v-if="deviation.orderBindings"
				:items="deviation.orderBindings || []"
				displayKey="order.orderNumber"
				idPath="order.id"
				@change="setOrders"
				@tag-click="openOrder"
			/>
		</div>
		<hr >

		<div class="action-description">
			<t-text-area
				:label="$t('actionDescription')"
				:value="deviation.actionDescription"
				height="100%"
				@input="setActionDescription"
			/>
		</div>

		<div class="action-type">
			<ActionTypeInput
				:value="deviation.actionType"
				@change="setActionType"
			/>
			<t-date-time-input
				:title="$t('actionDate')"
				:dateTimeObject="deviation.actionDate"
				noTime
				@date-input="setActionDate({ dateValue: $event, hasTime: null })"
				@time-input="setActionDate({ timeValue: $event, hasTime: Boolean($event) })"
			/>
		</div>
	</div>
</template>

<script>
import ClerkInput from '@/modules/clerks/ClerkInput.vue';
import DeviationTypeInput from '@/modules/deviations/DeviationTypeInput.vue';
import DeviationCauseInput from '@/modules/deviations/DeviationCauseInput.vue';
import GoodsTypesFormInput from '@/modules/goodsTypes/GoodsTypesFormInput';
import TripsSearchInput from '@/modules/trips/TripsSearchInput';
import OrdersMultiselect from '@/modules/orders/OrdersMultiselect';
import CustomerInput from '@/modules/customers/CustomerInput';
import VendorInput from '@/modules/vendors/VendorInput';
import TripAddressInput from '@/modules/tripAddresses/TripAddressInput';
import LicencePlates from './components/LicencePlates.vue';
import ActionTypeInput from './components/ActionTypeInput';
import DeviationStatusInput from './components/DeviationStatusInput';

import { mapActions } from './store/newDeviation.store.js';

import { eventBus } from '@/core';

export default {
	name: 'NewDeviationMain',
	components: {
		ClerkInput,
		DeviationTypeInput,
		DeviationCauseInput,
		GoodsTypesFormInput,
		CustomerInput,
		VendorInput,
		TripAddressInput,
		TripsSearchInput,
		OrdersMultiselect,
		LicencePlates,
		ActionTypeInput,
		DeviationStatusInput,
	},

	props: {
		deviation: Object,
	},

	data() {
		return {
		};
	},

	computed: {
		trip() {
			return this.deviation && Object.keys(this.deviation.trip).length ? this.deviation.trip : null;
		},
		orders() {
			return this.deviation.orderBindings &&
				this.deviation.orderBindings.length &&
				this.deviation.orderBindings.map(deviationOrder => ({
					...deviationOrder.order,
				}));
		},
	},

	methods: {
		...mapActions([
			'setClerk',
			'setDateTime',
			'setDeviationType',
			'setStatus',
			'setCustomerFault',
			'setMessage',
			'setDeviationCause',
			'setGoodsType',
			'setDiscoveredAt',
			'setDiscoveredBy',
			'setCustomer',
			'setVendor',
			'setTerminal',
			'setCost',
			'setLicencePlates',
			'setTrip',
			'setOrders',
			'addOrder',
			'setActionType',
			'setActionDescription',
			'setActionDate',
			'addTripOrders',
		]),

		openOrder(orderBinding) {
			eventBus.$emit('setEditOrder', orderBinding.order.id);
			eventBus.$emit('toggleNewOrderModal', true);
		},

		removeOrderBinding(index) {
			let newOrderBindings = [...this.deviation.orderBindings];
			newOrderBindings.splice(index, 1);
			this.setOrders(newOrderBindings);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.main {
	display: grid;
	row-gap: 20px;
	column-gap: 20px;
	grid-template-columns: 1fr 1fr;
	grid-template-areas:
		"top				top"
		".....				....."
		"message			details"
		".....				....."
		"customer-vendor	terminal-cost"
		"licenceplates		licenceplates"
		".....				....."
		"trip   			orders"
		".....				....."
		"action-description	action-type";
	margin-top: 20px;
	transition: 0.3s;
}

.top {
	grid-area: top;
	display: grid;
	column-gap: 20px;
	align-items: center;
	grid-template-columns: 1fr auto 1fr 1fr auto;
}

.message {
	grid-area: message;

	div {
		height: 100%;
	}
}

.details {
	grid-area: details;
	display: grid;
	row-gap: 20px;
	column-gap: 20px;
	grid-template-columns:	max-content
							1fr	;
	grid-template-rows: auto;
}

.customer-vendor {
	grid-area: customer-vendor;
	display: grid;
	row-gap: 20px;
	column-gap: 20px;
	grid-template-columns: 1fr 1fr;
}

.terminal-cost {
	grid-area: terminal-cost;
	display: grid;
	row-gap: 20px;
	column-gap: 20px;
	grid-template-columns: 2fr 1fr;
}

.licenceplates {
	grid-area: licenceplates;
}

.bottom {
	grid-area: bottom;
	display: grid;
	row-gap: 20px;
	column-gap: 20px;
	grid-template-columns: repeat(2, 1fr);
	grid-template-rows: auto;
}

.trip {
	grid-area: trip;
	display: grid;
	row-gap: 20px;
	column-gap: 20px;
	grid-template-columns: repeat(2, 1fr);
	grid-template-areas:
		"input input"
		"from  to"
		"etd   eta"
		"owner cargoCarrier"
}

.orders {
	grid-area: orders;
	display: grid;
	row-gap: 10px;
	column-gap: 20px;
	grid-template-columns: 1fr;
	grid-template-rows: minmax(min-content, max-content);
}

.action-description {
	grid-area: action-description;
}

.action-type {
	grid-area: action-type;
	display: grid;
	row-gap: 20px;
	column-gap: 20px;
	grid-template-columns: 1fr auto;
}

hr {
	grid-column: 1 / -1;
	height: 1px;
	background: get-color($border, 'default');
	width: 100%;
	margin-top: 0;
	margin-bottom: 0;
	border: 0;
	grid-column: span 2;
}
</style>
